import type { VacancyId } from '../config/vacancies';
import { DEFAULT_MIRROR_ID } from '../config/mirrors';

const PREFIX = 'portal:';

/** Ключи, существовавшие до введения зеркал. Подлежат разовой миграции. */
export const LEGACY_KEYS = [
  'portal_active_section',
  'portal_schedule_shifts',
  'portal_schedule_week',
  'portal_saved_project',
  'hr_portal_active_section',
] as const;

/** Ключи сессии — управляются useAuth, под неймспейс не попадают. */
export const SESSION_KEYS = ['portal_auth_user', 'portal_manager', 'portal_mirror'] as const;

const MIGRATION_MARKER = 'portal_ls_migrated_v1';

export interface StorageScope {
  mirror: string;
  vacancy: VacancyId;
  username: string;
}

/** portal:<mirror>:<vacancy>:<username>:<key> */
export function scopedKey(scope: StorageScope, key: string): string {
  return `${PREFIX}${scope.mirror}:${scope.vacancy}:${scope.username}:${key}`;
}

export function getScoped(scope: StorageScope, key: string): string | null {
  try {
    return localStorage.getItem(scopedKey(scope, key));
  } catch {
    return null;
  }
}

export function setScoped(scope: StorageScope, key: string, value: string): void {
  try {
    localStorage.setItem(scopedKey(scope, key), value);
  } catch {
    /* quota / приватный режим — не роняем UI */
  }
}

export function removeScoped(scope: StorageScope, key: string): void {
  try {
    localStorage.removeItem(scopedKey(scope, key));
  } catch {
    /* ignore */
  }
}

/**
 * Зачистка при выходе: сессия + легаси-ключи.
 *
 * ВАЖНО — что НЕ удаляется и почему: scoped-ключи (`portal:<mirror>:<vacancy>:<username>:*`)
 * сохраняются. Изоляцию между зеркалами и между людьми на общей машине даёт сам
 * неймспейс — чужие данные прочитать невозможно, потому что ключ содержит и зеркало,
 * и логин. А стирать их при каждом выходе означало бы регресс UX: сегодня расписание
 * и выбранный проект переживают логаут, и операторы на это рассчитывают.
 *
 * Полная зачистка `portal:*` доступна отдельно — `wipeAllScopedStorage()`,
 * на случай передачи компьютера другому сотруднику.
 */
export function clearAllPortalStorage(): void {
  try {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** Тотальная очистка портального состояния. В обычном флоу не вызывается. */
export function wipeAllScopedStorage(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    localStorage.removeItem(MIGRATION_MARKER);
  } catch {
    /* ignore */
  }
}

/**
 * Разовый перенос легаси-данных в скоуп зеркала `default`.
 * Выполняется только если целевой скоуп пуст, и только один раз (маркер).
 */
export function migrateLegacyStorage(username: string): void {
  try {
    if (!username) return;
    if (localStorage.getItem(MIGRATION_MARKER)) return;

    const operatorScope: StorageScope = {
      mirror: DEFAULT_MIRROR_ID,
      vacancy: 'chat-operator',
      username,
    };

    // hr_portal_active_section НЕ переносим: страница HR на CabinetShell не переезжает
    // и продолжает читать этот ключ напрямую (hr-manager/page.tsx:25,33).
    // Перенос дал бы мёртвую копию и разовый сброс раздела у HR-менеджеров.
    const moves: Array<[string, StorageScope, string]> = [
      ['portal_active_section', operatorScope, 'active_section'],
      ['portal_schedule_shifts', operatorScope, 'schedule_shifts'],
      ['portal_schedule_week', operatorScope, 'schedule_week'],
      ['portal_saved_project', operatorScope, 'saved_project'],
    ];

    moves.forEach(([legacyKey, scope, newKey]) => {
      const value = localStorage.getItem(legacyKey);
      if (value === null) return;
      if (getScoped(scope, newKey) !== null) return;  // не затираем уже существующее
      setScoped(scope, newKey, value);
    });

    LEGACY_KEYS.filter((k) => k !== 'hr_portal_active_section')
      .forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(MIGRATION_MARKER, '1');
  } catch {
    /* ignore */
  }
}
